import { z } from "zod";
// ─── Vault Folder ──────────────────────────────────────────
export const VaultFolderSchema = z.object({
    id: z.string().uuid(),
    parentId: z.string().uuid().nullable(),
    title: z.string().min(1).max(200),
    sortOrder: z.number().int().default(0),
    visibleToRoles: z.array(z.string()).min(1),
    // Folder-level toggle controlling whether portal users (magic-link
    // auth) for the same tenant can see documents inside this folder.
    // Independent from `visibleToRoles` (staff RBAC). Default false:
    // a brand-new folder is private to staff until explicitly shared.
    sharedWithPortal: z.boolean().default(false),
    archivedAt: z.string().datetime().nullable().default(null),
    archivedBy: z.string().email().nullable().default(null),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
export const VaultFolderCreateSchema = z.object({
    parentId: z.string().uuid().nullable().default(null),
    title: z.string().min(1).max(200),
    sortOrder: z.number().int().optional(),
    visibleToRoles: z.array(z.string()).min(1),
    // DB column defaults to false; callers who don't care about
    // portal sharing can omit this field entirely.
    sharedWithPortal: z.boolean().optional(),
});
export const VaultFolderUpdateSchema = VaultFolderCreateSchema.partial();
// ─── Vault Document ────────────────────────────────────────
export const ExtractionStatusEnum = z.enum(["pending", "processing", "complete", "failed"]);
/**
 * One placed field on a PDF *template* — the shape persisted in
 * `vault_documents.template_fields` and the input the New Signature
 * modal expands into per-signer fields when sending.
 *
 * Distinct from `SignatureFieldSchema` (signatures.ts), which is the
 * shape used on a concrete `signature_requests` row — that one carries
 * `assigneeEmail` because the signer is bound. Templates carry
 * `signerSlot` (a 1-based slot index) which the New Signature modal
 * maps to the picked signer's email at send time. Routine NDA templates
 * reserve slot 1 for the pre-authorised Latitude30 signer, then use later
 * slots for Party B signers and witnesses.
 *
 * Coords are PDF points, bottom-left origin (the wire shape used by
 * the public sign page and the post-completion PDF embed).
 */
export const TemplateFieldSchema = z.object({
    page: z.number().int().positive(),
    x: z.number().nonnegative(),
    y: z.number().nonnegative(),
    width: z.number().positive(),
    height: z.number().positive(),
    type: z.enum(["signature", "initials", "date", "text"]),
    signerSlot: z.number().int().positive(),
    label: z.string().trim().min(1).max(160).optional(),
    placeholder: z.string().trim().min(1).max(240).optional(),
    required: z.boolean().default(true),
});
export const VaultDocumentSchema = z.object({
    id: z.string().uuid(),
    folderId: z.string().uuid(),
    filename: z.string().min(1).max(500),
    title: z.string().min(1).max(500),
    blobKey: z.string().nullable(),
    mimeType: z.string().max(100),
    sizeBytes: z.number().int().nonnegative(),
    tags: z.array(z.string().min(1).max(50)),
    companyId: z.string().uuid().nullable(),
    uploadedBy: z.string().email(),
    archived: z.boolean().default(false),
    archivedAt: z.string().datetime().nullable().default(null),
    archivedBy: z.string().email().nullable().default(null),
    extractionStatus: ExtractionStatusEnum.default("pending"),
    extractedSummary: z.string().nullable().default(null),
    renewalDate: z.string().datetime().nullable().default(null),
    // Saved field positions for documents tagged `template:*`. Empty
    // for non-templates. Was missing from this schema, which made
    // apiFetch's response parse silently strip the array — the
    // "Fields missing" badge surfaced regardless of save success.
    templateFields: z.array(TemplateFieldSchema).default([]),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
export const TemplateFieldsUpdateSchema = z.object({
    fields: z.array(TemplateFieldSchema),
});
export const VaultDocumentCreateSchema = z.object({
    folderId: z.string().uuid(),
    filename: z.string().min(1).max(500),
    title: z.string().min(1).max(500),
    mimeType: z.string().max(100),
    sizeBytes: z.number().int().nonnegative(),
    tags: z.array(z.string().min(1).max(50)).default([]),
    companyId: z.string().uuid().nullable().optional(),
    // Required: the blob key returned by /documents/upload-token. The
    // client uploads the file directly to that key via signed URL, then
    // posts here with the same key so the DB row points at the right blob.
    blobKey: z.string().min(1).max(1000),
});
// Request body for POST /documents/upload-token. Validated at the
// route so folderId/filename/contentType can't arrive malformed or
// oversized (assessment H2 — this endpoint previously took an
// unvalidated `c.req.json()`).
export const VaultUploadTokenRequestSchema = z.object({
    folderId: z.string().uuid(),
    filename: z.string().min(1).max(500),
    contentType: z.string().min(1).max(100),
});
export const VaultDocumentUpdateSchema = z.object({
    title: z.string().min(1).max(500).optional(),
    tags: z.array(z.string().min(1).max(50)).optional(),
    companyId: z.string().uuid().nullable().optional(),
    // Legacy: kept for backwards compat. New code should use the dedicated
    // archive/unarchive endpoints. Setting archived=true here also stamps
    // archived_at server-side.
    archived: z.boolean().optional(),
});
export const VaultDocumentListQuerySchema = z.object({
    folderId: z.string().uuid().optional(),
    search: z.string().max(200).optional(),
    extractionStatus: ExtractionStatusEnum.optional(),
    // Legacy: equivalent to archivedOnly when true. Server still honors
    // it for back-compat; prefer includeArchived/archivedOnly.
    archived: z.coerce.boolean().optional(),
    // Admin-only opt-ins. Server enforces — non-admins always see active-only.
    includeArchived: z.coerce.boolean().optional(),
    archivedOnly: z.coerce.boolean().optional(),
    companyId: z.string().uuid().optional(),
    tag: z.string().max(50).optional(),
    // 200 because the New Signature Request picker needs every document
    // for selection in one shot. Don't lower without updating callers.
    limit: z.coerce.number().int().min(1).max(200).default(50),
    offset: z.coerce.number().int().min(0).default(0),
});
// ─── Audit ─────────────────────────────────────────────────
export const VaultAuditSchema = z.object({
    id: z.string().uuid(),
    documentId: z.string().uuid(),
    action: z.enum(["create", "update", "delete", "access", "download"]),
    userId: z.string().email(),
    details: z.record(z.string(), z.unknown()).optional(),
    createdAt: z.string().datetime(),
});
// ─── Response ──────────────────────────────────────────────
export const VaultDocumentListResponseSchema = z.object({
    documents: z.array(VaultDocumentSchema),
    total: z.number().int(),
});
export const VaultFolderListResponseSchema = z.object({
    folders: z.array(VaultFolderSchema),
});
/**
 * Client-rasterized PDF pages for smart template-field suggestion.
 * Staff Edit-fields sends up to 2 page images (page 1 + closing); the
 * API text-scans the blob and asks Claude vision for placements.
 */
export const SuggestTemplateFieldsPageSchema = z.object({
    page: z.number().int().positive(),
    /** Raw base64 (no data: URL prefix). */
    imageBase64: z.string().min(1).max(6_000_000),
    mediaType: z.enum(["image/png", "image/jpeg", "image/webp"]),
    /** PDF page width in points (media box). */
    pageWidthPt: z.number().positive(),
    /** PDF page height in points (media box). */
    pageHeightPt: z.number().positive(),
});
export const SuggestTemplateFieldsRequestSchema = z.object({
    pages: z.array(SuggestTemplateFieldsPageSchema).min(1).max(2),
});
export const SuggestTemplateFieldsResponseSchema = z.object({
    fields: z.array(TemplateFieldSchema),
    meta: z.object({
        mode: z.enum(["vision", "heuristic", "lab"]),
        pagesVisioned: z.array(z.number().int().positive()),
        textHitCount: z.number().int().nonnegative(),
        model: z.string().nullable(),
    }),
});
//# sourceMappingURL=documents.js.map